/**
 * W67 Phase 5.5 (2026-05-25) — HoverTooltip
 * W81 Mobile-Fix (2026-05-25) — Touch-Support via tabIndex + focus-within
 *
 * Styled-Tooltip-Wrapper fuer rich content (mehrzeilig, Tailwind-styled).
 * Im Gegensatz zu TermTooltip (statischer Glossar-Text) nimmt HoverTooltip
 * beliebigen ReactNode als Tooltip-Body — perfekt fuer dynamische Badges
 * (Quintile-Rang, Conviction-Score, Hype-Counts etc.).
 *
 * User-Feedback (2026-05-25): "zentrale Watchlist-Legende oben ist
 * schlechtes Design. Lieber Hover-Tooltip direkt am Pill."
 *
 * Verwendung:
 *   <HoverTooltip content={<>Mehrzeilig styled<br/>mit JSX</>}>
 *     <span className="bg-...">Pill-Inhalt</span>
 *   </HoverTooltip>
 *
 * Pure Tailwind + tabIndex/focus-within (kein Floating-UI noetig). Tooltip
 * erscheint UNTER dem Trigger (in den meisten Faellen passt das fuer
 * Watchlist-Cards).
 *
 * W81 Mobile-Trigger:
 *   - Desktop: Hover ueber den Wrapper → Tooltip via group-hover
 *   - Touch: Tap auf den Wrapper → fokussiert via tabIndex=0 → Tooltip
 *     via group-focus-within. Tap ausserhalb → Blur → Tooltip schliesst.
 *   - Nested-Button-Case (z.B. Score-Cell): Tap auf inneren Button setzt
 *     focus-within auf Wrapper → Tooltip + Button-onClick feuern beide.
 *     Akzeptabler Trade-off.
 */

import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

interface HoverTooltipProps {
  /** Der Inhalt der Tooltip-Bubble. Kann beliebige JSX sein. */
  content: ReactNode;
  /** Das Element auf dem gehovered wird. */
  children: ReactNode;
  /** Optionaler ClassName fuer das umschliessende span. */
  className?: string;
  /** Optionale Position des Tooltips. Default 'bottom'. */
  side?: 'top' | 'bottom';
  /** Optionale Breite des Tooltips in rem. Default 18 (~288px). */
  widthRem?: number;
  /**
   * W87 R4 (2026-05-27) — Tooltip-Ausrichtung horizontal.
   * 'left' (default): Tooltip startet links am Trigger.
   * 'right': Tooltip endet rechts am Trigger (fuer Trigger nahe rechtem Viewport-Rand).
   * 'center': zentriert ueber/unter dem Trigger.
   * User-Report: rechte Score-Cell-Tooltips wurden rechts abgeschnitten.
   */
  align?: 'left' | 'right' | 'center';
}

export function HoverTooltip({
  content,
  children,
  className,
  side = 'bottom',
  widthRem = 18,
  align = 'left',
}: HoverTooltipProps) {
  const positionClass =
    side === 'top'
      ? 'bottom-full mb-1'
      : 'top-full mt-1';
  // W87 R4 — Horizontale Ausrichtung: left-0 / right-0 / center
  const alignClass =
    align === 'right'
      ? 'right-0'
      : align === 'center'
        ? 'left-1/2 -translate-x-1/2'
        : 'left-0';
  return (
    <span
      // W81 Mobile-Fix — tabIndex macht den Wrapper Touch-fokussierbar.
      // focus:outline-none unterdrueckt den nativen Browser-Focus-Ring,
      // weil die Tooltip-Bubble selbst das visuelle Feedback ist.
      tabIndex={0}
      className={cn(
        'group relative inline-flex cursor-help focus:outline-none',
        className
      )}
    >
      {children}
      <span
        role="tooltip"
        className={cn(
          // W81 — group-focus-within zusaetzlich zu group-hover, damit
          // Touch-Geraete (kein :hover) den Tooltip ueber Tap-Fokus oeffnen.
          'pointer-events-none absolute z-50 hidden',
          // Audit 15.06.: group-hover auf echte Hover-Geräte beschränkt — sonst
          // klebte der Tooltip nach einem Tap auf Touch offen (wie SectorHeatmap).
          '[@media(hover:hover)]:group-hover:block group-focus-within:block group-focus:block',
          'rounded-md border border-cockpit-border-strong bg-cockpit-surface3',
          'px-3 py-2 text-[11px] leading-snug text-zinc-200 shadow-panel-hover',
          positionClass,
          alignClass
        )}
        style={{ width: `${widthRem}rem` }}
      >
        {content}
      </span>
    </span>
  );
}
